import { z } from 'zod';
import { request, useMutation, useQueryClient } from '@applyboard/ui-utils';
import { VasError } from '../common/errors/VasError';
import { getAccessToken } from '../common/utils/accessToken';
import { getBase64 } from '../common/utils/getBase64';
import { getPhoneDetails } from '../common/utils/getPhoneDetails';
import {
  DEFAULT_MIN_STRING_LENGTH,
  FILE_ARR_LENGTH_V2,
  FILE_NAME_MAX_LENGTH,
  GIVEN_NAME_MAX_LENGTH,
  STUDENT_ID_MAX_LENGTH,
  SUPPORTED_FILES_MIME_TO_EXTENSION_MAP,
  SURNAME_MAX_LENGTH,
} from '../pages/RbcGicFormNew/constants';
import { RbcGicFormTypeV2 } from '../pages/RbcGicFormNew/types';

export const RBC_VENDOR_STR = 'RBC';
const V2_CREATE_RBC_GIC_APPLICATION_URL = '/api/gic/v2/applications';
const V2_DOWNLOAD_GIC_FORM_URL = (applicationId: string) =>
  `/api/gic/v2/applications/${applicationId}/gic_form`;

const transformFile = async (
  inputFile: File,
  documentType: string,
): Promise<{ file_name: string; content: string }> => {
  const base64 = (await getBase64(inputFile)) as string;
  const randomId = Math.floor(Math.random() * 999999) + 100000;
  const fileExtension = SUPPORTED_FILES_MIME_TO_EXTENSION_MAP.get(inputFile.type);
  if (!fileExtension) {
    throw new Error('Unsupported file type');
  }
  const fileName = `${documentType}-${randomId}${fileExtension}`;

  return { file_name: fileName, content: base64 };
};

const addressSchema = z.object({
  apt_suite_number: z.string().optional(),
  street_name_and_number: z.string(),
  city: z.string(),
  state_province: z.string().optional(),
  country: z.string(),
  postal_code: z.string().optional(),
});

export const createRbcGicApplicationV2ReqSchema = z.object({
  vendor: z.literal(RBC_VENDOR_STR),
  recruitment_partner_id: z.string().optional(),
  student: z.object({
    prefix: z.string().optional(),
    given_name: z
      .string()
      .trim()
      .min(DEFAULT_MIN_STRING_LENGTH)
      .max(GIVEN_NAME_MAX_LENGTH)
      .optional(),
    surname: z.string().trim().max(SURNAME_MAX_LENGTH).optional(),
    date_of_birth: z.string(),
    student_id: z
      .string()
      .trim()
      .min(DEFAULT_MIN_STRING_LENGTH)
      .max(STUDENT_ID_MAX_LENGTH)
      .regex(/^[0-9]+$/, 'Student ID is an invalid format')
      .optional(),
  }),
  application_purpose: z.string(),
  occupation: z.object({
    employment_status: z.string(),
    level_of_study: z.string().optional(),
    occupation: z.string().optional(),
    occupation_other: z.string().optional(),
    company_name: z.string().optional(),
    phone_number: z.string().optional(),
    country_code: z.string().optional(),
  }),
  contact: z.object({
    phone_number: z.string(),
    country_code: z.string().optional(),
    is_mobile: z.boolean(),
    email: z.string().trim().email(),
    residential_address: addressSchema,
    mailing_address: addressSchema,
  }),
  passport: z.object({
    passport_number: z.string(),
    issuing_country: z.string(),
    issuing_place_or_authority: z.string(),
    expiry_date: z.string(),
  }),
  school: z.object({
    school_name: z.string(),
    province_of_study: z.string(),
    expected_arrival_date: z.string(),
    expected_graduation_date_month: z.string(),
    expected_graduation_date_year: z.string(),
  }),
  will_third_party_use: z.boolean(),
  tax: z.object({
    is_resident_of_canada: z.boolean(),
    canada_resident_province: z.string().optional(),
    province_of_study: z.string().optional(),
    is_resident_outside_canada: z.boolean().optional(),
    countries_of_residence: z.array(z.string()).optional(),
  }),
  politically_exposed_persons: z.object({
    is_politically_exposed_person: z.boolean(),
    full_name: z.string().optional(),
    role: z.string().optional(),
    relationship: z.string().optional(),
    source_of_wealth: z.array(z.string()).optional(),
    method_to_obtain_funds: z.array(z.string()).optional(),
  }),
  applyboard_application_package_consent: z.boolean(),
  source: z.string().trim().optional(),
  supporting_documents: z
    .array(
      z.object({
        file_name: z.string().min(DEFAULT_MIN_STRING_LENGTH).max(FILE_NAME_MAX_LENGTH),
        content: z.string(),
      }),
    )
    .length(FILE_ARR_LENGTH_V2),
});

export type CreateRbcGicApplicationReqV2Body = z.infer<typeof createRbcGicApplicationV2ReqSchema>;

export const createRbcGicApplicationV2ResSchema = z
  .object({
    data: z
      .object({
        id: z.string(),
        type: z.literal('application'),
        attributes: z
          .object({
            vendor: z.literal(RBC_VENDOR_STR),
            student: z
              .object({
                prefix: z.string().optional(),
                given_name: z.string().trim().optional(),
                surname: z.string().trim().optional(),
                date_of_birth: z.string(),
                student_id: z.string().trim().optional(),
              })
              .passthrough(),
            application_purpose: z.string(),
            occupation: z
              .object({
                employment_status: z.string(),
                level_of_study: z.string().optional(),
                occupation: z.string().optional(),
                company_name: z.string().optional(),
                phone_number: z.string().optional(),
                country_code: z.string().optional(),
              })
              .passthrough(),
            contact: z
              .object({
                phone_number: z.string().optional(),
                country_code: z.string().optional(),
                is_mobile: z.boolean(),
                email: z.string().trim().email(),
                residential_address: addressSchema,
                mailing_address: addressSchema,
              })
              .passthrough(),
            passport: z
              .object({
                passport_number: z.string(),
                issuing_country: z.string(),
                issuing_place_or_authority: z.string(),
                expiry_date: z.string(),
              })
              .passthrough(),
            school: z
              .object({
                school_name: z.string(),
                province_of_study: z.string(),
                expected_arrival_date: z.string(),
                expected_graduation_date_month: z.string(),
                expected_graduation_date_year: z.string(),
              })
              .passthrough(),
            will_third_party_use: z.boolean(),
            tax: z
              .object({
                is_resident_of_canada: z.boolean(),
                canada_resident_province: z.string().optional(),
                province_of_study: z.string().optional(),
                is_resident_outside_canada: z.boolean().optional(),
                countries_of_residence: z.array(z.string()).optional(),
              })
              .passthrough(),
            politically_exposed_persons: z
              .object({
                is_politically_exposed_person: z.boolean(),
                full_name: z.string().optional(),
                role: z.string().optional(),
                relationship: z.string().optional(),
                source_of_wealth: z.array(z.string()).optional(),
                method_to_obtain_funds: z.array(z.string()).optional(),
              })
              .passthrough(),
            applyboard_application_package_consent: z.boolean().optional(),
            accept_applyboard_terms_conditions: z.boolean().optional(),
            accept_gic_provider_terms_conditions: z.boolean().optional(),
            recruitment_partner_id: z.string().trim().optional().nullable(),
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export type CreateRbcGicApplicationResV2Body = z.infer<typeof createRbcGicApplicationV2ResSchema>;

export function useCreateRbcGicApplicationV2(recruitmentPartnerId: string) {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateRbcGicApplicationReqV2Body) => {
      const token = await getAccessToken(false);
      return request(
        V2_CREATE_RBC_GIC_APPLICATION_URL + `?recruitment_partner_id=${recruitmentPartnerId}`,
        {
          body: inputBody,
          method: 'POST',
          schema: createRbcGicApplicationV2ResSchema,
          authorization: token ? `Bearer ${token}` : undefined,
          throwError: async (response: Response) => {
            const jsonMessage = await response.json();
            throw new VasError(jsonMessage, jsonMessage.message);
          },
        },
      );
    },
  );

  const createRbcGicApplication = (
    params: CreateRbcGicApplicationReqV2Body,
    onSuccess?: (data: CreateRbcGicApplicationResV2Body) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (response: CreateRbcGicApplicationResV2Body) => {
        queryClient.invalidateQueries([V2_CREATE_RBC_GIC_APPLICATION_URL]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    createRbcGicApplication,
    isCreatingRbcGicApplication: isLoading,
    createRbcGicApplicationError: isError,
  };
}

export const formDataToCreateV2Body = async (
  inputData: RbcGicFormTypeV2,
  recruitmentPartnerId?: string,
  source?: string,
): Promise<CreateRbcGicApplicationReqV2Body> => {
  const letterOfAcceptanceBase64File = transformFile(
    inputData.letterOfAcceptanceFile[0].file,
    'letter-of-acceptance',
  );

  const allFiles = await Promise.all([letterOfAcceptanceBase64File]);
  // Fix dates
  // 1. Get date portion in local TZ
  // 2. Construct a new date from the date portion
  // 3. Get ISO date string from #2
  const dateOfBirth = new Intl.DateTimeFormat('en-CA').format(
    new Date(inputData.dateOfBirth.trim()),
  );
  // Expiry date
  const passportExpiryDate = new Intl.DateTimeFormat('en-CA').format(
    new Date(inputData.expiryDate.trim()),
  );
  // Arrival date
  const arrivalDate = new Intl.DateTimeFormat('en-CA').format(
    new Date(inputData.expectedArrivalDate.trim()),
  );

  // Contact phone number and country code
  const contactPhoneDetails = getPhoneDetails(inputData.phoneNumber);
  // Occupation phone number and country code
  const occupationPhoneDetails = getPhoneDetails(inputData.companyPhoneNumber);

  const body: CreateRbcGicApplicationReqV2Body = {
    vendor: RBC_VENDOR_STR,
    recruitment_partner_id: recruitmentPartnerId?.trim(),
    source: source?.trim(),
    student: {
      prefix: inputData.prefix?.trim(),
      date_of_birth: dateOfBirth,
      given_name: inputData.givenName?.trim(),
      surname: inputData.surname?.trim(),
      student_id: inputData.studentId?.trim(),
    },
    application_purpose: inputData.purpose?.trim(),
    occupation: {
      employment_status: inputData.employmentStatus?.trim(),
      level_of_study: inputData.levelOfStudy?.trim(),
      occupation: inputData.occupation?.trim(),
      occupation_other: inputData.occupationOther?.trim(),
      company_name: inputData.companyName?.trim(),
      phone_number:
        occupationPhoneDetails.phoneNumber != ''
          ? occupationPhoneDetails.phoneNumber
          : inputData.companyPhoneNumber?.trim(),
      country_code: occupationPhoneDetails.phoneCountryCode,
    },
    contact: {
      phone_number:
        contactPhoneDetails.phoneNumber != ''
          ? contactPhoneDetails.phoneNumber
          : inputData.phoneNumber?.trim(),
      country_code: contactPhoneDetails.phoneCountryCode,
      is_mobile: inputData.isMobileNumber,
      email: inputData.emailAddress?.trim(),
      residential_address: {
        apt_suite_number: inputData.aptOrSuiteResidential?.trim(),
        street_name_and_number: inputData.numberAndStreetNameResidential?.trim(),
        city: inputData.cityResidential?.trim(),
        country: inputData.countryResidential?.trim(),
        state_province: inputData.stateOrProvinceResidential?.trim(),
        postal_code: inputData.postalCodeResidential?.trim(),
      },
      mailing_address: {
        apt_suite_number: inputData.aptOrSuiteMailing?.trim(),
        street_name_and_number: inputData.numberAndStreetNameMailing?.trim(),
        city: inputData.cityMailing?.trim(),
        state_province: inputData.stateOrProvinceMailing?.trim(),
        country: inputData.countryMailing?.trim(),
        postal_code: inputData.postalCodeMailing?.trim(),
      },
    },
    passport: {
      passport_number: inputData.passportNumber?.trim(),
      issuing_country: inputData.issuingCountry?.trim(),
      issuing_place_or_authority: inputData.issuingPlaceOrAuthority?.trim(),
      expiry_date: passportExpiryDate,
    },
    school: {
      school_name: inputData.school?.trim(),
      province_of_study: inputData.provinceOfStudy?.trim(),
      expected_arrival_date: arrivalDate,
      expected_graduation_date_month: inputData.expectedGraduationMonth?.trim(),
      expected_graduation_date_year: inputData.expectedGraduationYear?.trim(),
    },
    will_third_party_use: inputData.isThirdPartyInvestment?.trim() === 'Yes',
    tax: {
      is_resident_of_canada: inputData.isCanadianResidentForTax?.trim() === 'Yes',
      canada_resident_province: inputData.canadianTaxProvince?.trim(),
      province_of_study: inputData.provinceOfStudy?.trim(),
      is_resident_outside_canada: inputData.isOtherCountryResidentForTax?.trim() === 'Yes',
      countries_of_residence: [
        inputData.otherCountryResidentForTaxCountry1?.trim() ?? 'CA', //TODO: Remove this when DTO is updated
        inputData.otherCountryResidentForTaxCountry2?.trim(),
        inputData.otherCountryResidentForTaxCountry3?.trim(),
      ].filter(country => country !== undefined),
    },
    politically_exposed_persons: {
      is_politically_exposed_person: inputData.hasPoliticallyImportantPositions?.trim() === 'Yes',
      full_name: inputData.politicallyImportantFullName?.trim(),
      role: inputData.politicallyImportantRole?.trim(),
      relationship: inputData.politicallyImportantRelationship?.trim(),
      source_of_wealth: inputData.sourceOfWealth.map(item => {
        return item === 'Other' ? inputData.sourceOfWealthOther : item;
      }),
      method_to_obtain_funds: inputData.gicFundSource.map(item => {
        return item === 'Other' ? inputData.gicFundSourceOther : item;
      }),
    },
    supporting_documents: allFiles,
    //TODO: This is not part of the form,
    applyboard_application_package_consent: true,
  };

  return createRbcGicApplicationV2ReqSchema.parse(body);
};

export const persistedGicApplicationV2ResSchema = z
  .object({
    data: z
      .object({
        id: z.string(),
        type: z.literal('application'),
        attributes: z
          .object({
            vendor: z.literal(RBC_VENDOR_STR),
            recruitment_partner_id: z.string().trim().optional(),
            student: z
              .object({
                given_name: z.string().trim().optional(),
                surname: z.string().trim().optional(),
                student_id: z.string().optional(),
              })
              .passthrough(),
            contact: z.object({
              email: z.string().trim().email(),
            }),
            passport: z
              .object({
                issuing_country: z.string(),
              })
              .passthrough(),
            school: z
              .object({
                province_of_study: z.string(),
              })
              .passthrough(),
            will_third_party_use: z.boolean(),
            accept_applyboard_terms_conditions: z.boolean().optional(),
            accept_gic_provider_terms_conditions: z.boolean().optional(),
            signed_at: z.date().optional(),
            verified_at: z.date().optional(),
            gic_id: z.string().optional(),
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export type PersistedGicApplicationV2ResBody = z.infer<typeof persistedGicApplicationV2ResSchema>;

export function useConsentAndSubmitRbcGicApplicationV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputParams: { id: string; verificationCode: string }) => {
      const headers: HeadersInit = {
        'x-verification-code': inputParams.verificationCode,
        'content-type': 'application/json',
      };

      const token = await getAccessToken(false);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(V2_CREATE_RBC_GIC_APPLICATION_URL + '/' + inputParams.id, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify({
          accept_applyboard_terms_conditions: true,
          accept_gic_provider_terms_conditions: true,
        }),
        redirect: 'follow',
        credentials: 'include', // this works even if the user is not authenticated
      });

      const jsonBody = await res.json();
      if (!res.ok) {
        throw new VasError(jsonBody, jsonBody.message);
      }

      return jsonBody;
    },
  );

  const consentAndSubmitRbcGicApplicationV2 = (
    params: { id: string; verificationCode: string },
    onSuccess?: (data: PersistedGicApplicationV2ResBody) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (data: PersistedGicApplicationV2ResBody) => {
        queryClient.invalidateQueries([`${V2_CREATE_RBC_GIC_APPLICATION_URL}/${params.id}`]);

        onSuccess?.(data);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    consentAndSubmitRbcGicApplicationV2,
    isConsentingAndSubmittingApplication: isLoading,
    consentSubmitApplicationError: isError,
  };
}

export function useVerifyRbcGicApplicationV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputParams: { id: string; verificationCode: string }) => {
      const res = await fetch(V2_CREATE_RBC_GIC_APPLICATION_URL + '/' + inputParams.id, {
        method: 'PATCH',
        headers: {
          'x-verification-code': inputParams.verificationCode,
        },
        body: JSON.stringify({}),
        redirect: 'follow',
      });

      const jsonBody = await res.json();
      if (!res.ok) {
        throw new VasError(jsonBody, 'Failed to verify code');
      }

      return jsonBody;
    },
  );

  const verifyRbcGicApplicationV2 = (
    params: { id: string; verificationCode: string },
    onSuccess?: (data: PersistedGicApplicationV2ResBody) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (data: PersistedGicApplicationV2ResBody) => {
        queryClient.invalidateQueries([`${V2_CREATE_RBC_GIC_APPLICATION_URL}/${params.id}`]);

        onSuccess?.(data);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    verifyRbcGicApplicationV2,
    isVerifyingApplication: isLoading,
    verifyApplicationError: isError,
  };
}

export const regenerateVerificationCodeResSchema = z.any();

export function useRegenerateVerificationCode() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(async (id: string) => {
    return request(`${V2_CREATE_RBC_GIC_APPLICATION_URL}/${id}/verification_code/resend`, {
      method: 'PATCH',
      schema: regenerateVerificationCodeResSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new VasError(jsonMessage, 'Resend verification code failed');
      },
    });
  });

  const regenerateVerificationCode = (
    id: string,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          `${V2_CREATE_RBC_GIC_APPLICATION_URL}/${id}/verification_code/resend`,
        ]);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    regenerateVerificationCode,
    isRegeneratingVerificationCode: isLoading,
    regenerateVerificationCodeError: isError,
  };
}

export function useDownloadGicForm(id: string, verificationCode: string): () => Promise<string> {
  return async () => {
    const response = await fetch(V2_DOWNLOAD_GIC_FORM_URL(id), {
      method: 'GET',
      headers: {
        'x-verification-code': verificationCode,
      },
      redirect: 'follow',
    });

    if (!response.ok) {
      const jsonBody = await response.json();
      throw new VasError(jsonBody, 'Failed to download the GIC form');
    }

    return await response.text();
  };
}
